import { Link, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, Building2, BarChart3, Settings, MessageSquare, Cog, Globe, Monitor, Activity, Palette, CreditCard, Tags, Factory, MapPin, Layers, Route, Sparkles, Headphones, FileText, Gift, Server, Package, Coins, Briefcase } from "lucide-react";
import { cn } from "@/lib/utils";

interface SuperAdminNavigationProps {
  isSuperAdmin: boolean;
  isLoading: boolean;
}

const SuperAdminNavigation = ({ isSuperAdmin, isLoading }: SuperAdminNavigationProps) => {
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="px-3 py-2">
        <div className="animate-pulse space-y-2">
          <div className="h-8 bg-muted rounded"></div>
          <div className="h-8 bg-muted rounded"></div>
          <div className="h-8 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  const superAdminLinks = [
    {
      href: "/super-admin",
      label: "Super Admin",
      icon: Shield,
    },
    {
      href: "/super-admin/users",
      label: "Users",
      icon: Users,
    },
    {
      href: "/super-admin/brands",
      label: "Brands",
      icon: Building2,
    },
    {
      href: "/super-admin/analytics",
      label: "Analytics",
      icon: BarChart3,
    },
    {
      href: "/super-admin/activity",
      label: "Activity",
      icon: Activity,
    },
    {
      href: "/super-admin/google-annotations",
      label: "Google Annotations",
      icon: Globe,
    },
    {
      href: "/super-admin/settings",
      label: "Settings",
      icon: Settings,
    },
    {
      href: "/super-admin/pin-protection",
      label: "PIN Protection",
      icon: Shield,
    },
    {
      href: "/super-admin/chat-logs",
      label: "Chat Logs",
      icon: MessageSquare,
    },
    {
      href: "/support",
      label: "Support",
      icon: Headphones,
    },
    {
      href: "/super-admin/provisioning-services",
      label: "Provisioning Services",
      icon: Cog,
    },
    {
      href: "/super-admin/reporting-dashboard-config",
      label: "Reporting Dashboard Config",
      icon: Monitor,
    },
    {
      href: "/super-admin/theme-management",
      label: "Theme Management",
      icon: Palette,
    },
    {
      href: "/super-admin/quota-management",
      label: "Quota Management",
      icon: CreditCard,
    },
    {
      href: "/super-admin/tags-management",
      label: "Tags Management",
      icon: Tags,
    },
    {
      href: "/super-admin/industries-management",
      label: "Industries Management",
      icon: Factory,
    },
    {
      href: "/super-admin/brand-features",
      label: "Brand Features",
      icon: Layers,
    },
    {
      href: "/super-admin/image-to-prompt",
      label: "Image to Prompt",
      icon: Sparkles,
    },
    {
      href: "/product-tours-admin",
      label: "Product Tours",
      icon: Route,
    },
    {
      href: "/fte-config",
      label: "FTE Configuration",
      icon: Sparkles,
    },
    {
      href: "/os-roadmap",
      label: "Bugs & Roadmap",
      icon: MapPin,
    },
    {
      href: "/menu-descriptions",
      label: "Menu Descriptions",
      icon: FileText,
    },
    {
      href: "/custom-subscription-offers",
      label: "Custom Offers",
      icon: Gift,
    },
    {
      href: "/super-admin/addon-definitions",
      label: "Addon Definitions",
      icon: Layers,
    },
    {
      href: "/super-admin/addon-grants",
      label: "Addon Grants",
      icon: Gift,
    },
    {
      href: "/admin/mcp-server",
      label: "MCP Server",
      icon: Server,
    },
    {
      href: "/admin/pricing-modules",
      label: "Pricing Modules",
      icon: Package,
    },
    {
      href: "/admin/pricing-credit-tiers",
      label: "AI Credit Tiers",
      icon: Coins,
    },
    {
      href: "/admin/consultant-applications",
      label: "Consultant Applications",
      icon: Briefcase,
    },
  ];

  return (
    <div className="border-t border-border pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="h-4 w-4 text-red-500" />
        <Badge variant="destructive" className="text-xs">
          Super Admin
        </Badge>
      </div>
      
      <nav className="space-y-1">
        {superAdminLinks.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.href;
          
          return (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors",
                isActive
                  ? "bg-red-100 text-red-900 dark:bg-red-900/20 dark:text-red-100"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <Icon className="h-4 w-4" />
              {link.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default SuperAdminNavigation;